/*
  ⚜️DD0 - Cart Block Web Components
  Lazy-initialized timer + upsell components for the cart drawer.

  Components do NO work at page load. They subscribe to a single global
  cart-drawer:open event (emitted by <cart-drawer>.open()) and only then:
    - <dd0-countdown-timer>  starts ticking once per session (sessionStorage)
    - <dd0-cart-upsell>      fetches recommendations API if strategy=recommendations
*/

(function () {
  'use strict';

  const SESSION_KEY = 'dd0:cart-countdown:expires-at';

  class DD0CountdownTimer extends HTMLElement {
    constructor() {
      super();
      this._started = false;
      this._intervalId = null;
      this._valueEl = null;
      this._duration = parseInt(this.dataset.duration || '300', 10);
    }

    connectedCallback() {
      this._valueEl = this.querySelector('[data-dd0-countdown-value]');
      this._listenForOpen();
    }

    disconnectedCallback() {
      if (this._intervalId) clearInterval(this._intervalId);
    }

    _listenForOpen() {
      const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      if (!drawer) return;

      drawer.addEventListener('cart-drawer:open', () => this._start(), { once: true });

      // Fallback: observe .active class on cart-drawer in case the event was emitted before this connected
      if (drawer.classList.contains('active')) {
        this._start();
        return;
      }
      const mo = new MutationObserver(() => {
        if (drawer.classList.contains('active')) {
          mo.disconnect();
          this._start();
        }
      });
      mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    _start() {
      if (this._started) return;
      this._started = true;

      let expiresAt = parseInt(sessionStorage.getItem(SESSION_KEY) || '0', 10);
      const now = Date.now();
      if (!expiresAt || expiresAt < now) {
        expiresAt = now + this._duration * 1000;
        sessionStorage.setItem(SESSION_KEY, String(expiresAt));
      }

      const tick = () => {
        const remainingMs = expiresAt - Date.now();
        if (remainingMs <= 0) {
          this._render(0);
          if (this._intervalId) clearInterval(this._intervalId);
          sessionStorage.removeItem(SESSION_KEY);
          return;
        }
        this._render(Math.ceil(remainingMs / 1000));
      };
      tick();
      this._intervalId = setInterval(tick, 1000);
    }

    _render(secondsLeft) {
      if (!this._valueEl) return;
      const m = Math.floor(secondsLeft / 60);
      const s = secondsLeft % 60;
      this._valueEl.textContent = String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
    }
  }

  class DD0CartUpsell extends HTMLElement {
    constructor() {
      super();
      this._loaded = false;
      this._strategy = this.dataset.strategy || 'manual';
      this._endpoint = this.dataset.endpoint || '';
      this._style = this.dataset.style || 'add_button';
      this._toggleElement = this.dataset.toggleElement || 'button';
      this._updatePrices = this.dataset.updatePrices === 'true';
    }

    connectedCallback() {
      this._listEl = this.querySelector('[data-dd0-upsell-list]');
      this._loadingEl = this.querySelector('[data-dd0-upsell-loading]');
      this._bindItems();

      if (this._strategy !== 'recommendations') return;
      this._listenForOpen();
    }

    _listenForOpen() {
      const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      if (!drawer) return;

      drawer.addEventListener('cart-drawer:open', () => this._fetch(), { once: true });

      if (drawer.classList.contains('active')) {
        this._fetch();
        return;
      }
      const mo = new MutationObserver(() => {
        if (drawer.classList.contains('active')) {
          mo.disconnect();
          this._fetch();
        }
      });
      mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    async _fetch() {
      if (this._loaded || !this._endpoint) return;
      this._loaded = true;
      try {
        const res = await fetch(this._endpoint);
        if (!res.ok) throw new Error('Recommendations fetch failed: ' + res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = doc.querySelector('[data-dd0-upsell-list]');
        if (incoming && this._listEl) {
          this._listEl.innerHTML = incoming.innerHTML;
          this._bindItems();
        }
        if (this._loadingEl) this._loadingEl.remove();
      } catch (err) {
        console.warn('[dd0-cart-upsell]', err);
        if (this._loadingEl) this._loadingEl.remove();
      }
    }

    _bindItems() {
      this.querySelectorAll('[data-dd0-upsell-form]').forEach((form) => {
        if (form._dd0Bound) return;
        form._dd0Bound = true;

        const item = form.closest('.dd0-upsells__item');
        const toggle = form.querySelector('[data-dd0-upsell-toggle]');
        const isToggleStyle = !!toggle;

        if (isToggleStyle) {
          toggle.addEventListener('change', () => {
            if (toggle.checked) this._addItem(form);
            else this._removeItem(form);
          });

          if (this._toggleElement === 'container' && item) {
            item.style.cursor = 'pointer';
            item.addEventListener('click', (e) => {
              if (e.target.closest('a, select, button, input, label')) return;
              toggle.checked = !toggle.checked;
              toggle.dispatchEvent(new Event('change'));
            });
          }

          if (toggle.checked && item && !item.dataset.dd0Preselected && !item.classList.contains('is-in-cart')) {
            item.dataset.dd0Preselected = 'true';
            this._addItem(form);
          }
        } else {
          form.addEventListener('submit', (e) => {
            e.preventDefault();
            this._addItem(form);
          });
        }

        if (this._updatePrices) {
          const variantSelect = form.parentElement && form.parentElement.querySelector('[data-dd0-upsell-variant-select]');
          if (variantSelect) {
            variantSelect.addEventListener('change', () => this._onVariantChange(form, variantSelect));
          }
        }
      });
    }

    _onVariantChange(form, select) {
      const opt = select.options[select.selectedIndex];
      if (!opt) return;
      const variantInput = form.querySelector('[data-dd0-upsell-variant-input]');
      if (variantInput) variantInput.value = opt.value;
      const item = form.closest('.dd0-upsells__item');
      if (!item) return;
      const priceEl = item.querySelector('[data-dd0-upsell-price]');
      if (!priceEl) return;
      const newPrice = parseInt(opt.dataset.price || '0', 10);
      if (!newPrice) return;
      priceEl.textContent = this._formatMoney(newPrice);
      item.dataset.basePrice = newPrice;
    }

    _formatMoney(cents) {
      const fmt = window.Shopify && window.Shopify.formatMoney;
      if (fmt) return fmt(cents);
      return (cents / 100).toFixed(2);
    }

    _adjustSubtotal(deltaCents) {
      if (!deltaCents) return;
      document.querySelectorAll('cart-drawer [data-cents]').forEach((el) => {
        const current = parseInt(el.dataset.cents || '0', 10);
        const original = parseInt(el.dataset.originalCents || el.dataset.cents || '0', 10);
        // Apply same proportional discount the cart already has, so optimistic estimate matches server
        const ratio = original > 0 ? current / original : 1;
        const adjustedDelta = Math.round(deltaCents * ratio);
        const next = current + adjustedDelta;
        el.dataset.cents = String(next);
        el.dataset.originalCents = String(original + deltaCents);
        const newAmount = (next / 100).toFixed(2).replace('.', ',');
        el.querySelectorAll('span').forEach((span) => {
          const m = span.textContent.match(/[\d.,]+/);
          if (m) span.textContent = span.textContent.replace(m[0], newAmount);
        });
        if (el.children.length === 0) {
          const m = el.textContent.match(/[\d.,]+/);
          if (m) el.textContent = el.textContent.replace(m[0], newAmount);
        }
      });
    }

    async _addItem(form) {
      const btn = form.querySelector('button[type="submit"]');
      const toggle = form.querySelector('[data-dd0-upsell-toggle]');
      if (btn) btn.disabled = true;
      try {
        const formData = new FormData(form);
        formData.append('sections', 'cart-drawer,cart-icon-bubble');
        const res = await fetch(window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: formData,
        });
        if (!res.ok) throw new Error('Add failed: ' + res.status);
        const json = await res.json();

        const drawer = document.querySelector('cart-drawer');
        if (drawer && json.sections) {
          drawer.renderContents({ id: json.id, sections: json.sections });
        }
      } catch (err) {
        console.warn('[dd0-cart-upsell] add error', err);
        if (toggle) toggle.checked = false;
      } finally {
        if (btn) btn.disabled = false;
      }
    }

    async _removeItem(form) {
      const variantInput = form.querySelector('[data-dd0-upsell-variant-input]');
      if (!variantInput) return;
      const variantId = variantInput.value;
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 0, sections: 'cart-drawer,cart-icon-bubble' }),
        });
        if (!res.ok) throw new Error('Remove failed: ' + res.status);
        const json = await res.json();
        const drawer = document.querySelector('cart-drawer');
        if (drawer && json.sections) {
          drawer.renderContents({ id: variantId, sections: json.sections });
        }
      } catch (err) {
        console.warn('[dd0-cart-upsell] remove error', err);
      }
    }
  }

  /*
    <dd0-gift-guard> — removes orphan free-gift lines (added by the
    Quantity Gifts product block) when the qualifying quantity is no longer
    in the cart. Rendered by Liquid only when the cart has a gift line, and
    re-connects on every drawer/cart re-render, so validation runs exactly
    when the cart changes. Reads the DOM rows — zero extra fetches.
  */
  class DD0GiftGuard extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this._validate());
    }

    async _validate() {
      if (window.Shopify && window.Shopify.designMode) return;
      // Gifts of the product page's own <quantity-gifts> are synced there — skip
      // them to avoid a double removal race; this guard covers every other page.
      const pdp = document.querySelector('quantity-gifts');
      const pdpHandle = pdp ? pdp.dataset.productHandle : null;
      const rows = Array.from(document.querySelectorAll('.cart-item[data-dd0-handle]'));
      const toRemove = [];
      rows
        .filter((row) => row.dataset.dd0GiftMain && row.dataset.dd0GiftMain !== pdpHandle)
        .forEach((gift) => {
          const min = parseInt(gift.dataset.dd0GiftMin, 10) || 1;
          const mainQty = rows
            .filter((row) => !row.dataset.dd0GiftMain && row.dataset.dd0Handle === gift.dataset.dd0GiftMain)
            .reduce((sum, row) => sum + (parseInt(row.dataset.dd0Qty, 10) || 0), 0);
          if (mainQty < min) toRemove.push(gift.dataset.dd0Key);
        });
      if (!toRemove.length) return;

      try {
        const drawer = document.querySelector('cart-drawer');
        const cartItemsEl = document.getElementById('main-cart-items');
        const sections = drawer
          ? 'cart-drawer,cart-icon-bubble'
          : `${cartItemsEl ? cartItemsEl.dataset.id + ',' : ''}cart-icon-bubble`;

        let json = null;
        for (let i = 0; i < toRemove.length; i++) {
          const body = { id: toRemove[i], quantity: 0 };
          if (i === toRemove.length - 1) body.sections = sections;
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          if (!res.ok) throw new Error('Gift removal failed: ' + res.status);
          if (i === toRemove.length - 1) json = await res.json();
        }
        if (!json || !json.sections) return;

        if (drawer) {
          if (drawer.classList.contains('active')) {
            drawer.renderContents({ id: toRemove[0], sections: json.sections });
          } else if (json.sections['cart-drawer']) {
            // Silent update — renderContents() force-opens the drawer (cart-drawer.js → this.open())
            const inner = document.getElementById('CartDrawer');
            if (inner) {
              inner.innerHTML = new DOMParser()
                .parseFromString(json.sections['cart-drawer'], 'text/html')
                .querySelector('#CartDrawer').innerHTML;
              const overlay = drawer.querySelector('#CartDrawer-Overlay');
              if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
            }
            if (json.sections['cart-icon-bubble']) {
              const bubble = document.getElementById('cart-icon-bubble');
              if (bubble) {
                const incoming = new DOMParser()
                  .parseFromString(json.sections['cart-icon-bubble'], 'text/html')
                  .querySelector('.shopify-section');
                if (incoming) bubble.innerHTML = incoming.innerHTML;
              }
            }
          }
          if (typeof json.item_count === 'number') drawer.classList.toggle('is-empty', json.item_count === 0);
        } else {
          if (cartItemsEl && json.sections[cartItemsEl.dataset.id]) {
            const incoming = new DOMParser()
              .parseFromString(json.sections[cartItemsEl.dataset.id], 'text/html')
              .querySelector('.js-contents');
            const target = cartItemsEl.querySelector('.js-contents');
            if (incoming && target) target.innerHTML = incoming.innerHTML;
          }
          if (json.sections['cart-icon-bubble']) {
            const bubble = document.getElementById('cart-icon-bubble');
            if (bubble) {
              const incoming = new DOMParser()
                .parseFromString(json.sections['cart-icon-bubble'], 'text/html')
                .querySelector('.shopify-section');
              if (incoming) bubble.innerHTML = incoming.innerHTML;
            }
          }
        }
      } catch (err) {
        console.warn('[dd0-gift-guard]', err);
      }
    }
  }

  if (!customElements.get('dd0-countdown-timer')) {
    customElements.define('dd0-countdown-timer', DD0CountdownTimer);
  }
  if (!customElements.get('dd0-cart-upsell')) {
    customElements.define('dd0-cart-upsell', DD0CartUpsell);
  }
  if (!customElements.get('dd0-gift-guard')) {
    customElements.define('dd0-gift-guard', DD0GiftGuard);
  }
})();
